import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, CalendarClock, ShieldCheck, MessageCircle, Phone, ThumbsUp, ThumbsDown, ArrowLeft, Wrench, Check, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { addressText, fmtDate, ACTIVE } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useLive } from "@/context/Realtime";
import { StatusBadge, Coin, UAvatar, Verified, Img } from "@/components/common";
import { TrackMap } from "@/components/Maps";
import SwipeButton from "@/components/SwipeButton";
import ImageUpload from "@/components/ImageUpload";

const Btn = ({ children, className = "bg-emerald-600 hover:bg-emerald-700 text-white", ...p }) => (
  <button {...p} className={`w-full h-12 rounded-full font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60 ${className}`}>{children}</button>
);

function useAction(reload) {
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      if (msg) toast.success(msg);
      await reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
}

function BidList({ job, reload }) {
  const [busy, run] = useAction(reload);
  if (!job.bids?.length) return <p data-testid="no-bids" className="text-sm text-slate-500 bg-white rounded-2xl p-4 border">Belum ada penawaran. Worker di sekitar akan segera mengirim tawaran.</p>;
  return (
    <div className="space-y-3" data-testid="bid-list">
      {job.bids.map((b) => (
        <div key={b.id} data-testid={`bid-card-${b.id}`} className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <UAvatar path={b.worker?.avatar} name={b.worker_name} className="w-11 h-11" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1"><p className="font-bold truncate">{b.worker_name}</p><Verified show={b.worker?.verified} /></div>
              <p className="text-xs text-slate-500">{b.worker?.distance_km} km · <ThumbsUp className="inline w-3 h-3" /> {b.worker?.thumbs_up}</p>
            </div>
            <Coin value={b.price_coins} showRp={false} className="text-emerald-700" />
          </div>
          {b.note && <p className="text-sm text-slate-600 mt-2 bg-slate-50 rounded-xl px-3 py-2">"{b.note}"</p>}
          <div className="flex gap-2 mt-3">
            <Link data-testid={`view-portfolio-${b.id}`} to={`/workers/${b.worker_id}`} className="flex-1 h-10 rounded-full border border-slate-300 text-sm font-bold grid place-items-center hover:bg-slate-50">Lihat Portofolio</Link>
            <button data-testid={`accept-bid-btn-${b.id}`} disabled={busy}
              onClick={() => run(() => api.post(`/jobs/${job.id}/bids/${b.id}/accept`), "Worker dipilih, koin ditahan di escrow")}
              className="flex-1 h-10 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-60">Pilih Worker</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewBox({ job, reload }) {
  const [thumbs, setThumbs] = useState("up");
  const [text, setText] = useState("");
  const [busy, run] = useAction(reload);
  if (job.review) {
    return (
      <div data-testid="review-display" className="bg-white rounded-2xl border p-4">
        <p className="text-xs font-bold text-slate-500 mb-1">Ulasan Anda</p>
        <p className="flex items-center gap-2 font-bold">{job.review.thumbs === "up" ? <ThumbsUp className="w-4 h-4 text-emerald-600" /> : <ThumbsDown className="w-4 h-4 text-red-500" />} {job.review.thumbs === "up" ? "Puas" : "Kurang puas"}</p>
        {job.review.text && <p className="text-sm text-slate-600 mt-1">{job.review.text}</p>}
      </div>
    );
  }
  if (!job.is_owner) return null;
  return (
    <div data-testid="rating-modal" className="bg-white rounded-2xl border p-4 space-y-3">
      <p className="font-display font-bold">Beri penilaian untuk worker</p>
      <div className="flex gap-2">
        {[["up", ThumbsUp, "Puas"], ["down", ThumbsDown, "Kurang"]].map(([v, I, l]) => (
          <button key={v} data-testid={`thumbs-${v}-btn`} onClick={() => setThumbs(v)}
            className={`flex-1 h-12 rounded-xl border-2 flex items-center justify-center gap-2 font-bold transition-colors ${thumbs === v ? (v === "up" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-red-400 bg-red-50 text-red-600") : "border-slate-200 text-slate-500"}`}>
            <I className="w-5 h-5" /> {l}
          </button>
        ))}
      </div>
      <textarea data-testid="review-text-input" className="field h-20 py-2" placeholder="Tulis ulasan Anda..." value={text} onChange={(e) => setText(e.target.value)} />
      <Btn data-testid="submit-review-btn" disabled={busy} onClick={() => run(() => api.post(`/jobs/${job.id}/review`, { thumbs, text }), "Terima kasih atas ulasannya!")}>Kirim Ulasan</Btn>
    </div>
  );
}

function OwnerActions({ job, reload }) {
  const [busy, run] = useAction(reload);
  const cancel = () => window.confirm("Batalkan pekerjaan ini? Koin escrow akan dikembalikan.") && run(() => api.post(`/jobs/${job.id}/cancel`), "Pekerjaan dibatalkan");
  return (
    <div className="space-y-4">
      {job.status === "open" && <><h3 className="font-display font-bold">Penawaran Worker ({job.bids?.length || 0})</h3><BidList job={job} reload={reload} /></>}
      {job.status === "submitted" && (
        <div className="space-y-3">
          <p className="text-sm text-teal-800 bg-teal-50 rounded-xl px-3 py-2">Worker telah mengunggah bukti pekerjaan. Periksa lalu konfirmasi untuk melepas koin escrow.</p>
          <Btn data-testid="confirm-complete-btn" disabled={busy} onClick={() => run(() => api.post(`/jobs/${job.id}/complete`), "Pekerjaan selesai! Koin diteruskan ke worker.")}>Konfirmasi Pekerjaan Selesai</Btn>
        </div>
      )}
      {job.status === "completed" && <ReviewBox job={job} reload={reload} />}
      {["open", "requested", "assigned"].includes(job.status) && (
        <Btn data-testid="cancel-job-btn" disabled={busy} onClick={cancel} className="bg-white border border-red-200 text-red-600 hover:bg-red-50">Batalkan Pekerjaan</Btn>
      )}
    </div>
  );
}

function WorkerActions({ job, reload, user }) {
  const [busy, run] = useAction(reload);
  const [price, setPrice] = useState(job.price_coins);
  const [note, setNote] = useState("");
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const step = (status, msg) => run(() => api.post(`/jobs/${job.id}/status`, { status }), msg);

  if (job.status === "open" && !job.is_owner) {
    if (job.my_bid) return <p data-testid="bid-sent-notice" className="text-sm font-semibold text-amber-800 bg-amber-50 rounded-xl px-4 py-3">Penawaran Anda ({job.my_bid.price_coins} koin) sudah terkirim. Menunggu pilihan pengguna.</p>;
    if (!user.is_worker) return null;
    return (
      <div data-testid="bid-input-form" className="bg-white rounded-2xl border p-4 space-y-3">
        <p className="font-display font-bold">Ajukan penawaran</p>
        <label className="block"><span className="text-xs font-semibold text-slate-600">Harga tawaran (koin)</span>
          <input data-testid="bid-price-input" type="number" min="1" className="field mt-1 font-mono" value={price} onChange={(e) => setPrice(e.target.value)} /></label>
        <input data-testid="bid-note-input" className="field" placeholder="Catatan untuk pengguna (opsional)" value={note} onChange={(e) => setNote(e.target.value)} />
        <SwipeButton label="Geser untuk ambil pesanan" onConfirm={() => run(() => api.post(`/jobs/${job.id}/bids`, { price_coins: Number(price), note }), "Penawaran terkirim!")} />
      </div>
    );
  }
  if (!job.is_assigned_worker) return null;
  if (job.status === "requested") {
    return (
      <div className="space-y-3">
        <SwipeButton label="Geser untuk terima pesanan" onConfirm={() => run(() => api.post(`/jobs/${job.id}/accept`), "Pesanan diterima")} />
        <Btn data-testid="reject-order-btn" disabled={busy} onClick={() => run(() => api.post(`/jobs/${job.id}/reject`), "Pesanan ditolak")} className="bg-white border border-red-200 text-red-600">Tolak Pesanan</Btn>
      </div>
    );
  }
  if (job.status === "assigned") return <Btn data-testid="start-trip-btn" disabled={busy} onClick={() => step("on_the_way", "Live tracking aktif")} className="bg-amber-500 hover:bg-amber-600 text-white">Berangkat ke Lokasi</Btn>;
  if (job.status === "on_the_way") return <Btn data-testid="confirm-arrival-btn" disabled={busy} onClick={() => step("arrived", "Kedatangan dikonfirmasi")} className="bg-amber-500 hover:bg-amber-600 text-white">Konfirmasi Kedatangan</Btn>;
  if (job.status === "arrived") return <Btn data-testid="start-job-btn" disabled={busy} onClick={() => step("in_progress", "Selamat bekerja!")} className="bg-amber-500 hover:bg-amber-600 text-white">Mulai Kerjakan</Btn>;
  if (job.status === "in_progress") {
    return (
      <div className="bg-white rounded-2xl border p-4 space-y-3">
        <p className="font-display font-bold">Unggah bukti selesai</p>
        <div className="grid grid-cols-2 gap-3">
          <ImageUpload testid="upload-before-photo" label="Foto Before" kind="proof" value={before} onChange={setBefore} />
          <ImageUpload testid="upload-after-photo" label="Foto After" kind="proof" value={after} onChange={setAfter} />
        </div>
        <Btn data-testid="finish-job-btn" disabled={busy || !before || !after} className="bg-amber-500 hover:bg-amber-600 text-white"
          onClick={() => run(() => api.post(`/jobs/${job.id}/proof`, { before_photo: before, after_photo: after }), "Bukti terkirim, menunggu konfirmasi pengguna")}>Selesai & Kirim Bukti</Btn>
      </div>
    );
  }
  if (job.status === "submitted") return <p className="text-sm text-teal-800 bg-teal-50 rounded-xl px-4 py-3">Menunggu pengguna mengonfirmasi pekerjaan selesai.</p>;
  if (job.status === "completed") return <p data-testid="worker-earning-summary" className="text-sm text-emerald-800 bg-emerald-50 rounded-xl px-4 py-3">Diterima {job.escrow_coins} koin, potongan aplikasi {job.fee_coins} koin{job.fee_percent != null ? ` (${job.fee_percent}%)` : ""}. Bersih {job.escrow_coins - job.fee_coins} koin.</p>;
  return null;
}

function Party({ title, p, showWa }) {
  if (!p) return null;
  return (
    <div className="flex items-center gap-3 bg-white rounded-2xl border p-3">
      <UAvatar path={p.avatar} name={p.name} className="w-11 h-11" />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-slate-500 font-semibold">{title}</p>
        <div className="flex items-center gap-1"><p className="font-bold truncate">{p.name}</p><Verified show={p.verified} /></div>
      </div>
      {showWa && p.whatsapp && (
        <a data-testid="call-worker-btn" href={`https://wa.me/${p.whatsapp.replace(/^0/, "62").replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
          className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center"><Phone className="w-4 h-4" /></a>
      )}
    </div>
  );
}

function JobTimeline({ job }) {
  const order = ["open", "requested", "assigned", "on_the_way", "arrived", "in_progress", "submitted", "completed"];
  const steps = [
    [job.mode === "direct" ? "requested" : "open", job.mode === "direct" ? "Pesanan dibuat" : "Pekerjaan diposting", job.created_at],
    ["assigned", job.mode === "direct" ? "Diterima worker" : "Worker dipilih", null],
    ["on_the_way", "Worker menuju lokasi", job.on_the_way_at],
    ["arrived", "Worker tiba di lokasi", job.arrived_at],
    ["in_progress", "Sedang dikerjakan", job.in_progress_at],
    ["submitted", "Bukti before/after diunggah", job.submitted_at],
    ["completed", "Selesai & koin diteruskan", job.completed_at],
  ];
  const failed = ["cancelled", "rejected"].includes(job.status);
  const cur = order.indexOf(job.status);
  return (
    <div data-testid="job-timeline" className="bg-white rounded-2xl border p-4">
      <p className="font-display font-bold mb-3">Tahapan Pekerjaan</p>
      <ol className="relative">
        {steps.map(([st, label, at], i) => {
          const done = !failed && order.indexOf(st) <= cur;
          const now = !failed && st === job.status;
          const last = i === steps.length - 1 && !failed;
          return (
            <li key={st} data-testid={`timeline-step-${st}`} className="relative flex gap-3 pb-4 last:pb-0">
              {!last && <span className={`absolute left-[11px] top-6 bottom-0 w-0.5 ${done && order.indexOf(st) < cur ? "bg-emerald-500" : "bg-slate-200"}`} />}
              <span className={`relative z-10 w-6 h-6 shrink-0 rounded-full grid place-items-center ${done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400 border border-slate-200"} ${now ? "ring-4 ring-emerald-100" : ""}`}>
                {done ? <Check className="w-3.5 h-3.5" /> : <span className="text-[10px] font-bold">{i + 1}</span>}
              </span>
              <div className="pt-0.5">
                <p className={`text-sm font-bold ${done ? "text-slate-900" : "text-slate-400"}`}>{label}</p>
                {at && done && <p className="text-[11px] text-slate-500">{fmtDate(at)}</p>}
                {now && !at && <p className="text-[11px] text-emerald-700 font-semibold">Tahap saat ini</p>}
              </div>
            </li>
          );
        })}
        {failed && (
          <li className="relative flex gap-3"><span className="w-6 h-6 rounded-full bg-red-500 text-white grid place-items-center"><X className="w-3.5 h-3.5" /></span>
            <p className="text-sm font-bold text-red-600 pt-0.5">{job.status === "cancelled" ? "Dibatalkan" : "Ditolak worker"} · {fmtDate(job.updated_at)}</p></li>
        )}
      </ol>
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  const { data: job, reload, setData } = useFetch(`/jobs/${id}`, { poll: 30000, live: (e) => e.type === "job" && e.job_id === id });
  useLive((e) => {
    if (e.type === "track" && e.job_id === id) setData((j) => (j ? { ...j, worker_location: e.location } : j));
  });
  const reloadAll = async () => { await reload(); refreshUser(); };
  if (!job) return <div className="h-64 rounded-2xl bg-slate-200 animate-pulse" />;
  const tracking = ACTIVE.includes(job.status) && job.worker_location;
  const hasChat = job.worker_id && (job.is_owner || job.is_assigned_worker) && job.status !== "requested";

  return (
    <div data-testid="job-detail-page" className="space-y-4 fade-up">
      <button data-testid="back-btn" onClick={() => nav(-1)} className="flex items-center gap-1 text-sm font-semibold text-slate-600"><ArrowLeft className="w-4 h-4" /> Kembali</button>
      <div>
        <div className="flex items-center gap-2 flex-wrap"><StatusBadge status={job.status} /><span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">{job.category} · {job.mode === "direct" ? "Booking Langsung" : "Post Job"}</span></div>
        <h1 data-testid="job-detail-title" className="font-display text-2xl font-extrabold mt-2">{job.title}</h1>
        <Coin value={job.price_coins} className="text-emerald-700 mt-1" />
      </div>
      {job.escrow_coins > 0 && job.status !== "completed" && (
        <p data-testid="escrow-notice-banner" className="text-xs text-blue-800 bg-blue-50 rounded-xl px-3 py-2 flex gap-2"><ShieldCheck className="w-4 h-4 shrink-0" />{job.escrow_coins} koin ditahan sistem (escrow) hingga pekerjaan dikonfirmasi selesai.</p>
      )}
      <div className="bg-white rounded-2xl border p-4 space-y-2 text-sm">
        <p className="flex gap-2"><MapPin className="w-4 h-4 shrink-0 text-slate-400 mt-0.5" /><span data-testid="job-detail-address">{addressText(job.address)}</span></p>
        {job.bring_tools && <p data-testid="job-bring-tools-badge" className="flex gap-2 font-semibold text-blue-800"><Wrench className="w-4 h-4" />Wajib Membawa Alat Kerja</p>}
        {job.schedule_at && <p className="flex gap-2"><CalendarClock className="w-4 h-4 text-slate-400" />Jadwal: {fmtDate(job.schedule_at)}</p>}
        {job.distance_km != null && <p className="text-xs font-mono text-slate-500">Jarak dari Anda: {job.distance_km} km</p>}
        {job.description && <p className="text-slate-600 pt-2 border-t">{job.description}</p>}
      </div>
      <TrackMap job={job} worker={tracking ? job.worker_location : null} />
      {tracking && <p className="text-[11px] text-slate-500 -mt-2">Posisi worker diperbarui {fmtDate(job.worker_location.updated_at)}</p>}
      <JobTimeline job={job} />
      <Party title={job.is_owner ? "Worker" : "Pemesan"} p={job.is_owner ? job.worker : job.owner} showWa={hasChat && job.status !== "completed"} />
      {hasChat && (
        <Link data-testid="chat-worker-btn" to={`/chat/${job.id}`} className="w-full h-11 rounded-full border border-slate-300 bg-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-slate-50"><MessageCircle className="w-4 h-4" /> Buka Chat</Link>
      )}
      {(job.before_photo || job.after_photo) && (
        <div className="grid grid-cols-2 gap-3" data-testid="proof-photos">
          <div><p className="text-xs font-bold text-slate-500 mb-1">Before</p><Img path={job.before_photo} className="w-full h-32 rounded-xl" /></div>
          <div><p className="text-xs font-bold text-slate-500 mb-1">After</p><Img path={job.after_photo} className="w-full h-32 rounded-xl" /></div>
        </div>
      )}
      {job.is_owner ? <OwnerActions job={job} reload={reloadAll} /> : <WorkerActions job={job} reload={reloadAll} user={user} />}
    </div>
  );
}
